import { useState } from "react";
import { PackageCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STEPS = ["placed", "packed", "shipped", "delivered"];

export default function TrackOrder() {
  const [form, setForm] = useState({ order_number: "", phone: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setBusy(true);
    setOrder(null);
    try {
      const r = await api.post("/orders/track", form);
      setOrder(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-x py-12 max-w-2xl" data-testid="track-order-page">
      <p className="eyebrow">GUEST TRACKING</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Track your order</h1>
      <p className="text-ink-2 text-sm mt-2">Order number and the phone number used at checkout — no account needed.</p>

      <form onSubmit={track} className="mt-8 bg-white border border-brand/10 rounded-xl p-6 space-y-4">
        <div>
          <Label htmlFor="to-number">Order number</Label>
          <Input id="to-number" required placeholder="ORD-000123" value={form.order_number}
            onChange={(e) => setForm({ ...form, order_number: e.target.value.trim() })} className="font-mono" data-testid="track-order-number" />
        </div>
        <div>
          <Label htmlFor="to-phone">Phone</Label>
          <Input id="to-phone" required placeholder="10-digit mobile" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="font-mono" data-testid="track-order-phone" />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-2 text-ivory rounded-full h-11 btn-press" data-testid="track-order-submit">
          {busy ? "Checking…" : "Track order"}
        </Button>
      </form>

      {order && (
        <div className="mt-8 bg-white border border-brand/10 rounded-xl p-6 animate-fade-up" data-testid="track-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="font-mono font-medium" data-testid="track-order-id">{order.order_number}</p>
            <span className="text-xs font-mono uppercase tracking-widest text-brand bg-brand-light px-3 py-1 rounded-full" data-testid="track-status">{order.status}</span>
          </div>
          <p className="text-sm text-ink-2 mt-1">Placed {fmtDateTime(order.created_at)} · {inr(order.total)} · {order.items?.length} item(s)</p>

          <div className="flex items-center mt-6" data-testid="track-steps">
            {STEPS.map((s, i) => {
              const reached = STEPS.indexOf(order.status) >= i || order.status === "delivered";
              return (
                <div key={s} className="flex-1 flex items-center">
                  <div className={`h-3 w-3 rounded-full shrink-0 ${reached ? "bg-gold" : "bg-brand/15"}`} />
                  {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${STEPS.indexOf(order.status) > i ? "bg-gold" : "bg-brand/15"}`} />}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-wide text-ink-2 mt-1">
            {STEPS.map((s) => <span key={s}>{s}</span>)}
          </div>

          {order.timeline?.length > 0 && (
            <ul className="mt-6 space-y-2.5 border-t border-brand/10 pt-4" data-testid="track-timeline">
              {[...order.timeline].reverse().map((t, i) => (
                <li key={i} className="text-sm flex gap-3">
                  <PackageCheck size={15} className="text-gold shrink-0 mt-0.5" />
                  <span><span className="font-medium capitalize">{t.status}</span>
                    {t.note ? ` — ${t.note}` : ""}
                    <span className="block text-xs font-mono text-ink-2">{fmtDateTime(t.at)}</span></span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
